// ═══════════════════════════════════════════════════════════════════
// VIDEO MODAL — YouTube embed inside an accessible dialog.
//
// Triggers are anchors marked [data-video-modal-open] whose href is the real
// YouTube watch link, so without JS the button still opens the video. With JS
// the click is intercepted and the video plays inside .video-modal instead.
//
// The iframe ships without a src: it is injected on open and removed on
// close, so the YouTube player is never downloaded on page load and closing
// the modal always stops playback.
// ═══════════════════════════════════════════════════════════════════
(function () {
    'use strict';

    const EMBED_BASE = 'https://www.youtube-nocookie.com/embed/';
    const FOCUSABLE = 'a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
    const wired = new WeakSet();

    if (!document.querySelector('[data-video-modal-open]')) return;

    function modalFor(trigger) {
        const id = trigger.getAttribute('aria-controls');
        return (id && document.getElementById(id)) || document.querySelector('.video-modal');
    }

    function embedUrl(videoId) {
        return EMBED_BASE + encodeURIComponent(videoId) + '?autoplay=1&rel=0';
    }

    function wire(modal) {
        if (wired.has(modal)) return;
        wired.add(modal);

        const frame = modal.querySelector('iframe');
        const closeButton = modal.querySelector('[data-video-modal-close]');
        let lastFocused = null;

        function isOpen() {
            return modal.classList.contains('is-open');
        }

        function open(videoId) {
            if (frame && videoId && !frame.getAttribute('src')) {
                frame.setAttribute('src', embedUrl(videoId));
            }

            lastFocused = document.activeElement;
            modal.classList.add('is-open');
            modal.setAttribute('aria-hidden', 'false');
            document.documentElement.style.overflow = 'hidden';
            if (closeButton) closeButton.focus();
        }

        function close() {
            if (!isOpen()) return;

            modal.classList.remove('is-open');
            modal.setAttribute('aria-hidden', 'true');
            document.documentElement.style.overflow = '';
            if (frame) frame.removeAttribute('src');
            if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
        }

        function openFrom(event) {
            const trigger = event.target.closest ? event.target.closest('[data-video-modal-open]') : null;
            if (!trigger || modalFor(trigger) !== modal) return;

            event.preventDefault();
            open(trigger.getAttribute('data-video-id'));
        }

        // Close button, or a click on the overlay padding around the dialog.
        function closeFrom(event) {
            if (event.target === modal || (event.target.closest && event.target.closest('[data-video-modal-close]'))) {
                close();
            }
        }

        function onKeydown(event) {
            if (!isOpen()) return;

            if (event.key === 'Escape' || event.key === 'Esc') {
                event.preventDefault();
                close();
                return;
            }

            if (event.key !== 'Tab') return;

            // Keep keyboard focus inside the dialog while it is open.
            const focusables = Array.from(modal.querySelectorAll(FOCUSABLE)).filter(function (element) {
                return element.offsetParent !== null;
            });
            if (!focusables.length) return;

            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }

        document.addEventListener('click', openFrom);
        modal.addEventListener('click', closeFrom);
        document.addEventListener('keydown', onKeydown);
    }

    document.querySelectorAll('[data-video-modal-open]').forEach(function (trigger) {
        const modal = modalFor(trigger);
        if (modal) wire(modal);
    });
})();